/* Bastar Arts — product.js
   Variant picker, media gallery, sticky add-to-cart. No page reloads. */
(function () {
  'use strict';

  /* ---------- Variant picker ---------- */
  class BAVariantPicker extends HTMLElement {
    connectedCallback() {
      try {
        this.productData = JSON.parse(this.querySelector('[data-product-json]').textContent);
      } catch (err) { return; }
      this.form = document.querySelector(`form[data-product-form][data-product-id="${this.dataset.productId}"]`);
      /* Inventory counts aren't in product.json — the section emits them separately */
      this.inventory = {};
      const invEl = document.querySelector('[data-inventory-json]');
      if (invEl) {
        try { this.inventory = JSON.parse(invEl.textContent); } catch (err) { /* ignore */ }
      }
      this.addEventListener('change', () => this.onChange());
    }

    get selectedOptions() {
      return Array.from(this.querySelectorAll('fieldset')).map((set) => {
        const checked = set.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    onChange() {
      const options = this.selectedOptions;
      const variant = this.productData.variants.find((v) => v.options.every((opt, i) => opt === options[i]));
      this.updateAvailabilityStates(options);
      this.updateUI(variant);
    }

    updateAvailabilityStates(options) {
      /* Mark option values that lead to no available variant, per position */
      this.querySelectorAll('fieldset').forEach((set, position) => {
        set.querySelectorAll('input').forEach((input) => {
          const candidate = options.slice();
          candidate[position] = input.value;
          const match = this.productData.variants.find((v) => v.options.every((opt, i) => opt === candidate[i]));
          const label = set.querySelector(`label[for="${input.id}"]`);
          if (label) label.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
    }

    updateUI(variant) {
      const button = this.form && this.form.querySelector('[type="submit"]');
      const buttonText = button && button.querySelector('span');
      const priceEl = document.querySelector(`[data-price-wrapper="${this.dataset.productId}"]`);
      const skuEl = document.querySelector('[data-product-sku]');
      const stockEl = document.querySelector('[data-product-stock]');

      if (!variant) {
        if (button) { button.setAttribute('aria-disabled', 'true'); button.disabled = true; }
        if (buttonText) buttonText.textContent = window.variantStrings.unavailable;
        return;
      }

      /* hidden id input */
      if (this.form) this.form.querySelector('input[name="id"]').value = variant.id;

      /* URL without reload */
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`);

      /* price */
      if (priceEl) {
        const money = (cents) => this.money(cents);
        const current = priceEl.querySelector('.ba-price__current');
        const compare = priceEl.querySelector('.ba-price__compare');
        if (current) current.textContent = money(variant.price);
        if (compare) {
          const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
          compare.hidden = !onSale;
          if (onSale) compare.textContent = money(variant.compare_at_price);
        }
      }

      /* sku */
      if (skuEl) {
        skuEl.hidden = !variant.sku;
        const value = skuEl.querySelector('[data-sku-value]');
        if (value) value.textContent = variant.sku || '';
      }

      /* stock + button */
      if (button && buttonText) {
        if (variant.available) {
          button.removeAttribute('aria-disabled');
          button.disabled = false;
          buttonText.textContent = window.variantStrings.addToCart;
        } else {
          button.setAttribute('aria-disabled', 'true');
          button.disabled = true;
          buttonText.textContent = window.variantStrings.soldOut;
        }
      }
      if (stockEl) this.updateStock(stockEl, variant);

      /* savings pill */
      const saveEl = document.querySelector('[data-product-save]');
      if (saveEl) {
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        saveEl.hidden = !onSale;
        if (onSale) {
          const percent = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          saveEl.textContent = (saveEl.dataset.label || 'Save [percent]%').replace('[percent]', percent);
        }
      }

      /* selected value labels */
      this.querySelectorAll('fieldset').forEach((set, i) => {
        const valueEl = set.querySelector('[data-selected-value]');
        if (valueEl) valueEl.textContent = variant.options[i];
      });

      /* media */
      if (variant.featured_media) {
        const gallery = document.querySelector('ba-media-gallery');
        if (gallery) gallery.showMedia(variant.featured_media.id);
      }

      /* sticky bar mirrors state */
      const stickyBtn = document.querySelector('[data-sticky-atc] .button');
      if (stickyBtn) {
        stickyBtn.disabled = !variant.available;
        stickyBtn.textContent = variant.available ? window.variantStrings.addToCart : window.variantStrings.soldOut;
      }
      const stickyPrice = document.querySelector('[data-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = this.money(variant.price);

      document.dispatchEvent(new CustomEvent('ba:variant:change', { detail: { variant } }));
    }

    money(cents) {
      const formatted = (cents / 100).toLocaleString(document.documentElement.lang || 'en', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
      return (this.dataset.currencySymbol || '₹') + formatted;
    }

    /* Mirrors the Liquid stock states in sections/main-product.liquid */
    updateStock(el, variant) {
      const strings = window.variantStrings || {};
      const inv = this.inventory[variant.id];
      const threshold = parseInt(el.dataset.lowThreshold, 10) || 5;
      let state = 'in';
      let qty = 0;

      if (!variant.available) {
        state = 'out';
      } else if (!inv || !inv.t) {
        state = 'in';
      } else {
        qty = inv.q;
        if (qty <= 0) state = inv.c ? 'backorder' : 'out';
        else if (qty <= threshold) state = 'low';
        else state = 'count';
      }

      const text = {
        count: (strings.inStockCount || '[count] in stock').replace('[count]', qty),
        low: (strings.lowStock || 'Only [count] left').replace('[count]', qty),
        in: strings.inStock || 'In stock',
        backorder: strings.backorder || 'Made to order',
        out: strings.outOfStock || strings.soldOut || 'Out of stock'
      }[state];

      ['in', 'count', 'low', 'backorder', 'out'].forEach((s) => el.classList.toggle('ba-stock--' + s, s === state));
      const textEl = el.querySelector('[data-stock-text]');
      if (textEl) textEl.textContent = text;

      const bar = el.querySelector('[data-stock-bar]');
      if (bar) {
        bar.hidden = state !== 'low';
        const fill = bar.firstElementChild;
        if (fill && state === 'low') fill.style.width = Math.max(6, Math.round((qty / threshold) * 100)) + '%';
      }
    }
  }
  customElements.define('ba-variant-picker', BAVariantPicker);

  /* ---------- Media gallery ---------- */
  class BAMediaGallery extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-media-id]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb-for]'));
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.showMedia(thumb.dataset.thumbFor));
      });
      /* zoom: open image in a simple lightbox */
      this.addEventListener('click', (e) => {
        const img = e.target.closest('.ba-media-item img');
        if (!img) return;
        const item = img.closest('[data-zoom-src]');
        if (item) this.openLightbox(item.dataset.zoomSrc, img.alt);
      });
    }
    showMedia(id) {
      id = String(id);
      this.items.forEach((item) => { item.hidden = item.dataset.mediaId !== id; });
      this.thumbs.forEach((thumb) => thumb.setAttribute('aria-current', thumb.dataset.thumbFor === id ? 'true' : 'false'));
    }
    openLightbox(src, alt) {
      let box = document.getElementById('BAProductLightbox');
      if (!box) {
        box = document.createElement('div');
        box.id = 'BAProductLightbox';
        box.setAttribute('role', 'dialog');
        box.setAttribute('aria-modal', 'true');
        box.setAttribute('aria-label', alt || 'Product image');
        box.style.cssText = 'position:fixed;inset:0;z-index:100;background:rgba(46,28,15,.92);display:flex;align-items:center;justify-content:center;padding:4vmin;cursor:zoom-out';
        box.innerHTML = '<img style="max-width:100%;max-height:100%;object-fit:contain"><button type="button" aria-label="Close" style="position:absolute;top:16px;right:16px;background:none;border:none;color:#f6eedd;font-size:28px;cursor:pointer;line-height:1">&times;</button>';
        const close = () => { box.remove(); document.removeEventListener('keydown', onKey); this._zoomOpener && this._zoomOpener.focus(); };
        const onKey = (e) => { if (e.key === 'Escape') close(); };
        box.addEventListener('click', close);
        document.addEventListener('keydown', onKey);
        document.body.appendChild(box);
      }
      this._zoomOpener = document.activeElement;
      const img = box.querySelector('img');
      img.src = src;
      img.alt = alt || '';
      box.querySelector('button').focus();
    }
  }
  customElements.define('ba-media-gallery', BAMediaGallery);

  /* ---------- Sticky mobile add-to-cart ---------- */
  const sticky = document.querySelector('[data-sticky-atc]');
  const buyBox = document.querySelector('[data-buy-buttons]');
  if (sticky && buyBox && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      sticky.classList.toggle('is-visible', !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0);
    });
    io.observe(buyBox);
    const stickyBtn = sticky.querySelector('.button');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', () => {
        const form = document.querySelector('form[data-product-form]');
        if (form) form.requestSubmit();
      });
    }
  }

  /* ---------- Recently viewed (localStorage, no dependencies) ---------- */
  const rvHolder = document.querySelector('[data-recently-viewed]');
  const current = document.querySelector('[data-product-handle]');
  try {
    const KEY = 'ba:recently-viewed';
    const list = JSON.parse(localStorage.getItem(KEY) || '[]');
    if (current) {
      const handle = current.dataset.productHandle;
      const next = [handle].concat(list.filter((h) => h !== handle)).slice(0, 9);
      localStorage.setItem(KEY, JSON.stringify(next));
      if (rvHolder) renderRecentlyViewed(list.filter((h) => h !== handle).slice(0, 4));
    } else if (rvHolder) {
      renderRecentlyViewed(list.slice(0, 4));
    }
  } catch (err) { /* private mode */ }

  function renderRecentlyViewed(handles) {
    if (!handles.length) return;
    Promise.all(handles.map((h) =>
      fetch(`/products/${h}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null)
    )).then((products) => {
      const valid = products.filter(Boolean);
      if (!valid.length) return;
      const grid = rvHolder.querySelector('[data-rv-grid]');
      grid.innerHTML = valid.map((p) => {
        const img = p.featured_image ? p.featured_image.replace(/(\.[a-z]+)(\?|$)/, '_500x$1$2') : '';
        const price = (p.price / 100).toLocaleString('en-IN');
        return `<div class="ba-card">
          <div class="ba-card__media">${img ? `<img src="${img}" alt="" loading="lazy" width="500" height="500">` : ''}</div>
          <div class="ba-card__info">
            <h3 class="ba-card__title"><a href="/products/${p.handle}">${p.title}</a></h3>
            <div class="ba-price"><span class="ba-price__current">₹${price}</span></div>
          </div>
        </div>`;
      }).join('');
      rvHolder.hidden = false;
    });
  }
})();
